import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { AppAccessService, NO_ACCESS_ROUTE } from '@/core/services/app-access.service';

/**
 * Gate de entrada a la app: una cuenta sin NINGÚN rol no pasa de la pantalla de bloqueo.
 *
 * Va en TODAS las rutas de primer nivel, junto a `MsalGuard` y siempre después de él: primero hay
 * que saber quién es la persona, después si su cuenta sirve. Autenticado ≠ autorizado, y sin este
 * guard "autenticado" era suficiente para entrar a todo lo que no fuera Calidad o Talento Humano.
 *
 * Distinto de `forbiddenUrlTree()`/`/forbidden`: ahí falta UN rol para UNA pantalla y el usuario
 * sigue teniendo app utilizable, así que se le dice qué rol pedir. Acá no hay nada que mostrar —
 * ni sidebar ni módulos ni peticiones que no terminen en 403 — y lo único accionable es cambiar de
 * cuenta o pedir acceso.
 *
 * Es síncrono (lee el claim `roles` del token) para no meter una llamada de red delante de cada
 * navegación; ver `AppAccessService`.
 */
export const appAccessGuard: CanActivateFn & CanActivateChildFn = () => {
    const access = inject(AppAccessService);
    const router = inject(Router);

    // Sin sesión no se decide nada: es trabajo de MsalGuard, que ya está redirigiendo al login.
    if (!access.isAuthenticated) {
        return true;
    }

    if (access.hasNoRoles()) {
        return router.createUrlTree([NO_ACCESS_ROUTE]);
    }

    return true;
};
